'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/context/LanguageContext';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQPage() {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqsEN: FAQItem[] = [
    {
      question: "What if my need is less than the specified requirements in the pricing list?",
      answer: "No problem at all — contact us! No task is too small. We're happy to help with smaller requests and will find a solution that fits your needs and budget.",
    },
    {
      question: "How long does it take to build a website?",
      answer: "A typical starter website takes 1–2 weeks. More complex projects with backend functionality can take 3–6 weeks depending on scope. We'll give you a clear timeline before we start.",
    },
    {
      question: "Do I need to sign a long-term contract?",
      answer: "No long-term contracts required. Hosting & maintenance plans run month-to-month and can be cancelled with 30 days' notice. We believe in earning your trust every month.",
    },
    {
      question: "Can I update the website myself after it's built?",
      answer: "Yes — we can set up a simple content management system so you can make basic updates yourself. For more advanced changes, we offer hourly update packages.",
    },
    {
      question: "Do you offer IT support for private individuals?",
      answer: "Absolutely. We offer private IT support at 495 DKK/hour covering everything from computer troubleshooting and WiFi setup to software installation and device configuration.",
    },
    {
      question: "What areas do you serve?",
      answer: "We primarily serve businesses and individuals in Vejle, Kolding, and Fredericia, but we work with clients across Denmark — and many tasks can be handled remotely.",
    },
    {
      question: "What technologies do you use?",
      answer: "We use modern, proven technologies: Next.js and React for web, React Native or Flutter for mobile apps, and Supabase or similar for backend and databases. We choose the right tool for each project.",
    },
    {
      question: "Can you take over an existing website or project?",
      answer: "Yes. We're experienced in taking over existing codebases, migrating websites, and continuing development on projects started elsewhere. Just send us the details and we'll assess the scope.",
    },
  ];

  const faqsDA: FAQItem[] = [
    {
      question: "Hvad hvis mit behov er mindre end de angivne krav på prislisten?",
      answer: "Ingen problem — kontakt os! Ingen opgave er for lille. Vi hjælper gerne med mindre forespørgsler og finder en løsning, der passer til dine behov og budget.",
    },
    {
      question: "Hvor lang tid tager det at bygge en hjemmeside?",
      answer: "En typisk starter-hjemmeside tager 1–2 uger. Mere komplekse projekter med backend-funktionalitet kan tage 3–6 uger afhængigt af omfanget. Vi giver dig en klar tidsplan, inden vi starter.",
    },
    {
      question: "Skal jeg skrive under på en langsigtet kontrakt?",
      answer: "Nej, ingen langsigtede kontrakter. Hosting- og vedligeholdelsesplaner kører måned for måned og kan opsiges med 30 dages varsel. Vi tror på at fortjene din tillid hver måned.",
    },
    {
      question: "Kan jeg selv opdatere hjemmesiden efter den er bygget?",
      answer: "Ja — vi kan opsætte et enkelt indholdsstyringssystem, så du selv kan foretage grundlæggende opdateringer. Til mere avancerede ændringer tilbyder vi timepakker til opdateringer.",
    },
    {
      question: "Tilbyder I IT-support til private?",
      answer: "Absolut. Vi tilbyder privat IT-support til 495 DKK/time og dækker alt fra computerfejlfinding og WiFi-opsætning til softwareinstallation og enhedskonfiguration.",
    },
    {
      question: "Hvilke områder betjener I?",
      answer: "Vi betjener primært virksomheder og privatpersoner i Vejle, Kolding og Fredericia, men vi arbejder med kunder i hele Danmark — og mange opgaver kan håndteres på afstand.",
    },
    {
      question: "Hvilke teknologier bruger I?",
      answer: "Vi bruger moderne, gennemprøvede teknologier: Next.js og React til web, React Native eller Flutter til mobilapps og Supabase eller lignende til backend og databaser. Vi vælger det rigtige værktøj til hvert projekt.",
    },
    {
      question: "Kan I overtage en eksisterende hjemmeside eller et projekt?",
      answer: "Ja. Vi har erfaring med at overtage eksisterende kodebaser, migrere hjemmesider og fortsætte udviklingen på projekter startet andetsteds. Send os blot detaljerne, så vurderer vi omfanget.",
    },
  ];

  const faqs = language === 'da' ? faqsDA : faqsEN;

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-blackout text-monitor">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-gold border border-gold/30 rounded-full px-4 py-1.5 mb-6">
            {t('faq.label')}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-monitor mb-4 leading-tight">
            {t('faq.title')}
          </h1>
          <p className="text-monitor/50 text-sm sm:text-base max-w-xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index
                  ? 'border-gold/40 bg-white/5' :'border-white/8 bg-white/2 hover:border-white/15'
              }`}
            >
              <button
                onClick={() => toggle(index)}
                className="w-full flex items-center justify-between px-5 sm:px-6 py-4 sm:py-5 text-left gap-4"
                aria-expanded={openIndex === index}
              >
                <span className="text-sm sm:text-base font-semibold text-monitor leading-snug">
                  {faq.question}
                </span>
                <span
                  className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center transition-all duration-300 ${
                    openIndex === index
                      ? 'border-gold bg-gold text-blackout rotate-45' :'border-white/20 text-monitor/40'
                  }`}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </span>
              </button>

              {openIndex === index && (
                <div className="px-5 sm:px-6 pb-5">
                  <div className="border-t border-white/8 pt-4">
                    <p className="text-monitor/60 text-sm sm:text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 px-4 sm:px-6 text-center">
        <div className="max-w-xl mx-auto border border-white/8 rounded-2xl p-8 sm:p-10 bg-white/2">
          <p className="text-monitor/50 text-sm mb-2">{t('faq.ctaLabel')}</p>
          <h2 className="text-xl sm:text-2xl font-black text-monitor mb-6">{t('faq.ctaTitle')}</h2>
          <a
            href="/home#contact"
            className="gold-shimmer-btn text-blackout text-xs font-black uppercase tracking-widest px-6 py-3 rounded-full transition-all hover:shadow-gold-glow inline-block"
          >
            {t('faq.ctaButton')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
